import {
  RealizationDepartmentDto,
  RealizationDepartmentStatusDto,
  RealizationLogTypeDto,
} from '@/api/generated';

export type RealizationTranslation = {
  card: {
    download: string;
    downloadedAt: string;
    guests: string;
    history: string;
    lead: string;
    leadPlaceholder: string;
    neverDownloaded: string;
    openOffer: string;
  };
  department: Record<RealizationDepartmentDto, string>;
  departmentPopover: {
    changedBy: string;
    download: string;
    downloadedBy: string;
    nobody: string;
    noContent: string;
    responsible: string;
    responsiblePlaceholder: string;
    status: string;
  };
  filter: {
    clearSearch: string;
    search: string;
    state: Record<'all' | 'done' | 'missingPeople' | 'noLead' | 'open', string>;
  };
  history: {
    action: Record<RealizationLogTypeDto, string>;
    empty: string;
    title: string;
    titleForOffer: string;
    whole: string;
  };
  list: {
    collapseAll: string;
    columns: {
      customer: string;
      event: string;
      guests: string;
      lead: string;
      location: string;
      progress: string;
      time: string;
    };
    expandAll: string;
    stats: {
      done: string;
      events: string;
      missingPeople: string;
      noLead: string;
    };
    today: string;
  };
  mode: {
    calendar: string;
    realizations: string;
  };
  row: {
    assignLead: string;
    collapse: string;
    departmentTooltip: string;
    done: string;
    expand: string;
    markDoneHint: string;
    owner: string;
    undoDone: string;
  };
  status: Record<RealizationDepartmentStatusDto, string>;
  toolbar: {
    allDepartments: string;
    day: string;
    downloadAll: string;
    downloadDepartment: string;
    next: string;
    pickDay: string;
    previous: string;
    tabHistory: string;
    tabList: string;
    today: string;
    week: string;
  };
  view: {
    clearFilters: string;
    empty: string;
    emptyForDepartment: string;
    goNext: string;
    noMatches: string;
  };
};

export const realizationTranslation: RealizationTranslation = {
  card: {
    download: 'Pobierz całość',
    downloadedAt: 'Pobrano {{date}}',
    guests: '{{count}} os.',
    history: 'Historia',
    lead: 'Kierownik realizacji',
    leadPlaceholder: 'Przypisz kierownika',
    neverDownloaded: 'Jeszcze nikt nie pobrał',
    openOffer: 'Otwórz ofertę',
  },
  department: {
    [RealizationDepartmentDto.Decoration]: 'Dekoracje',
    [RealizationDepartmentDto.Drinks]: 'Napoje',
    [RealizationDepartmentDto.Equipment]: 'Sprzęt',
    [RealizationDepartmentDto.Kitchen]: 'Kuchnia',
    [RealizationDepartmentDto.Magazine]: 'Magazyn',
    [RealizationDepartmentDto.Pastry]: 'Cukiernia',
    [RealizationDepartmentDto.Service]: 'Obsługa',
    [RealizationDepartmentDto.Transport]: 'Transport',
  },
  departmentPopover: {
    changedBy: '{{status}}: {{user}}, {{date}}',
    download: 'Pobierz PDF działu',
    downloadedBy: 'Ostatnie pobranie: {{user}}, {{date}}',
    nobody: 'bez osoby',
    noContent: 'Oferta nie ma pozycji dla tego działu — PDF będzie pusty',
    responsible: 'Osoba odpowiedzialna',
    responsiblePlaceholder: 'Przypisz osobę',
    status: 'Stan',
  },
  filter: {
    clearSearch: 'Wyczyść wyszukiwanie',
    search: 'Szukaj po nazwie, kliencie, miejscu',
    state: {
      all: 'Wszystkie',
      done: 'Gotowe',
      missingPeople: 'Brakuje osób',
      noLead: 'Bez kierownika',
      open: 'Do zrobienia',
    },
  },
  history: {
    action: {
      [RealizationLogTypeDto.DepartmentDone]: '{{department}}: gotowe',
      [RealizationLogTypeDto.DepartmentNotApplicable]: '{{department}}: nie dotyczy',
      [RealizationLogTypeDto.DepartmentReopened]: '{{department}}: z powrotem do zrobienia',
      [RealizationLogTypeDto.Downloaded]: 'Pobrano zlecenie realizacji — {{department}}',
      [RealizationLogTypeDto.LeadAssigned]: 'Kierownik realizacji: {{subject}}',
      [RealizationLogTypeDto.LeadCleared]: 'Usunięto kierownika realizacji',
      [RealizationLogTypeDto.ResponsibleAssigned]: '{{department}}: odpowiada {{subject}}',
      [RealizationLogTypeDto.ResponsibleCleared]: '{{department}}: usunięto osobę odpowiedzialną',
    },
    empty: 'Brak wpisów w historii',
    title: 'Historia realizacji',
    titleForOffer: 'Historia — {{offer}}',
    whole: 'całość',
  },
  list: {
    collapseAll: 'Zwiń wszystkie',
    columns: {
      customer: 'Klient',
      event: 'Wydarzenie',
      guests: 'Osób',
      lead: 'Kierownik',
      location: 'Miejsce',
      progress: 'Działy',
      time: 'Godziny',
    },
    expandAll: 'Rozwiń wszystkie',
    stats: {
      done: 'Gotowe',
      events: 'Wydarzenia',
      missingPeople: 'Brakuje osób',
      noLead: 'Bez kierownika',
    },
    today: 'dziś',
  },
  mode: {
    calendar: 'Kalendarz',
    realizations: 'Realizacje',
  },
  row: {
    assignLead: 'Przypisz',
    collapse: 'Ukryj szczegóły',
    departmentTooltip: '{{department}}: {{status}}, {{person}}',
    done: 'Gotowe',
    expand: 'Pokaż szczegóły',
    markDoneHint: '{{department}}: oznacz jako gotowe',
    owner: 'Opiekun: {{name}}',
    undoDone: '{{department}}: cofnij gotowe',
  },
  status: {
    [RealizationDepartmentStatusDto.Done]: 'Gotowe',
    [RealizationDepartmentStatusDto.NotApplicable]: 'Nie dotyczy',
    [RealizationDepartmentStatusDto.Todo]: 'Do zrobienia',
  },
  toolbar: {
    allDepartments: 'Wszystkie działy',
    day: 'Dzień',
    downloadAll: 'Pobierz wszystkie realizacje',
    downloadDepartment: 'Pobierz dla działu: {{department}}',
    next: 'Następny',
    pickDay: 'Przejdź do dnia',
    previous: 'Poprzedni',
    tabHistory: 'Historia',
    tabList: 'Lista',
    today: 'Dziś',
    week: 'Tydzień',
  },
  view: {
    clearFilters: 'Wyczyść filtry',
    empty: 'W tym okresie nie ma realizacji.',
    emptyForDepartment: 'W tym okresie dział {{department}} nie ma nic do zrobienia.',
    goNext: 'Pokaż następny okres',
    noMatches: 'Żadna realizacja nie pasuje do filtrów.',
  },
};
